/**
 * 服务端计时（见方案 2.3 与契约里的"防呆"要求）。
 *
 * **核心约束：已用时间必须基于服务端返回的时间戳计算，绝不能用本地 Date.now() 累加。**
 * 原因是客户端时间不可信 —— 孩子（或家长）把系统时间往前调一小时，本地累加就会显示错乱。
 *
 * 实现方式（两层保险）：
 *  1. **锚点**：每次拿到服务端响应，记下 `performance.now()` 与那一刻服务端累计的
 *     `actualSeconds`。`performance.now()` 是**单调时钟**，不受系统时间修改影响，
 *     所以"服务端确认的秒数 + 单调时钟走过的秒数"就是可信的已用时间。
 *  2. **不信任本地时间戳**：绝不解析 `startedAt` 再与 `new Date()` 相减 ——
 *     那正是"改本机时间就错乱"的来源。`startedAt` 只用于文案展示。
 *
 * 三层时间对齐（本地显示 → 服务端 heartbeat → 服务端 finish 结算）：
 *  - 本地每 30 秒发一次 heartbeat，顺便用响应里的 `actualSeconds` **重新锚定**，
 *    避免长计时累积误差；
 *  - 断网时 heartbeat 静默失败，本地继续按单调时钟走 ——
 *    "断网重连后计时不丢"靠的就是这条（重连后第一次 heartbeat 会把锚点拉回服务端的值，
 *    因此本地多走的、服务端没确认的部分会被纠正回来，宁可少算不可多算）。
 *
 * ## ⚠️ 2026-10-13 修掉的那个 bug：「每 30 秒从 0 重新数」
 *
 * 用户实测：点「开始」后计时正常走到 **30 秒**，然后跳回 0 重新数。
 * 30 秒正是心跳周期（`HEARTBEAT_INTERVAL_SECONDS`），所以问题就出在"重新锚定"这一步：
 *
 *  - **触发点**：心跳响应被上游解码成了 `actualSeconds: 0`
 *    （真实原因见 `lib/api/checkins.ts` 的 `normalizeTimerResponse`：
 *    心跳响应用的是平铺的 `checkinId`、没有 `id`，于是解码器落到兜底对象）；
 *  - **放大器**：本 hook 的 `reanchor()` 无条件地把锚点设成"现在 = 那个值"，
 *    于是表针立刻回到 0，再按单调时钟往上走 —— 精确表现为"每 30 秒重来一次"。
 *
 * 修法分两层，**任一层单独都不够**：
 *  1. 上游解码器认平铺的 `checkinId`（治本）；
 *  2. 本 hook 的锚点**只能前进，不能后退**（`@fht/shared` 的 `timer-clock.ts`）。
 * 第 2 层是关键：它让"上游给了一个偏小的值"这件事**不再有破坏性**，
 * 因此下一次字段改名、乱序响应、跨版本部署都不会再让用户的表针倒退。
 *
 * 判据全部抽在 `@fht/shared` 的 `timer-clock.ts` 里（纯函数，可直接测）——
 * 这里只负责"把纯函数接到 React 的渲染时机上"。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  HEARTBEAT_INTERVAL_SECONDS,
  advanceTimerClock,
  initialTimerAnchor,
  type CheckinStatus,
  type TimerAnchor,
} from '@fht/shared';
import { heartbeatCheckin } from './api/checkins';
import type { CheckinTimerResponse } from './api/types';

/**
 * 这个 hook 真正读的**全部**字段。
 *
 * 为什么不直接要求 `Checkin`：三条队列（今天的作业 / 超时 / 还有效）与今日打卡页
 * 手上的形状各不相同（`Checkin` / `HomeworkCheckinView` / `OverdueRowEntry`），
 * 让它们为了喂这个 hook 各自去拼一个完整的 `Checkin`（含 `startedAt`、`valueDone`…）
 * 等于**伪造业务字段** —— 而这个 hook 一个都不读。
 * `Checkin` 天然满足这个更窄的接口，所以既有调用方可以整个传进来。
 */
export interface ServerTimerSource {
  /** **checkinId**。 */
  id: string;
  status: CheckinStatus;
  /** 服务端确认过的已用秒数；`null` = 服务端还没说过（**不是 0**）。 */
  actualSeconds: number | null;
}

export interface ServerTimerState {
  /** 已用秒数（服务端确认 + 本地单调推进）。 */
  elapsedSeconds: number;
  /** 是否正在计时（running 状态）。 */
  isRunning: boolean;
  /** 最近一次心跳是否失败（用于提示"网络不太好"）。 */
  heartbeatFailed: boolean;
  /** 最近一次与服务端同步的时刻（用于"上次同步 xx 秒前"提示）。 */
  lastSyncAt: number | null;
}

/** 单调时钟：优先 performance.now()，退化到 Date.now()（老环境）。 */
function monotonicNow(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

/**
 * 服务端给的秒数 → 一个"可以拿去重新锚定"的数。
 *
 * ⚠️ `null` / `undefined`（**这次响应没说**）返回 `null`，与 `0`
 * （服务端说"确实没走过时间"）严格区分：调用方见到 `null` 时必须**跳过这一拍**。
 * 把 `null` 当 0 用，就是 2026-10-13 那个 bug 的形态。
 */
function toServerSeconds(value: number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : null;
}

export function useServerTimer(
  checkin: ServerTimerSource | null,
  options: { onSync?: (response: CheckinTimerResponse) => void } = {},
): ServerTimerState {
  // 锚点：单调时钟读数 + 那一刻服务端确认的秒数 + 已经显示过的最大秒数
  const anchorRef = useRef<TimerAnchor>(initialTimerAnchor(monotonicNow()));
  /** 当前锚点属于哪一条打卡 —— 变了才允许归零（见下面的重新锚定 effect）。 */
  const activeCheckinIdRef = useRef<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [heartbeatFailed, setHeartbeatFailed] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<number | null>(null);
  const onSyncRef = useRef(options.onSync);
  onSyncRef.current = options.onSync;

  const status = checkin?.status ?? null;
  const isRunning = status === 'running';
  const checkinId = checkin?.id ?? null;
  /** `null` = 服务端还没说过秒数（不是 0）。 */
  const serverSeconds = toServerSeconds(checkin?.actualSeconds);

  /** 把一次读数写进锚点并同步到界面。 */
  const applyReading = useCallback((observedServerSeconds?: number) => {
    const reading = advanceTimerClock(anchorRef.current, monotonicNow(), observedServerSeconds);
    anchorRef.current = reading.anchor;
    setElapsedSeconds(reading.elapsedSeconds);
    return reading;
  }, []);

  /**
   * 用服务端的值重新锚定。
   *
   * 传 `null`/省略 = "服务端这次没说" → **什么都不做**（保留当前锚点与显示）。
   * 这条分支是防呆的关键：锚点一旦被一个解不出来的响应写成 0，表针就会归零。
   */
  const reanchor = useCallback(
    (nextServerSeconds?: number | null) => {
      const seconds = toServerSeconds(nextServerSeconds);
      if (seconds === null) return;
      applyReading(seconds);
      setLastSyncAt(Date.now());
    },
    [applyReading],
  );

  // 打卡记录变化（或服务端秒数变化）时重新锚定
  useEffect(() => {
    /**
     * 换了一条打卡 = 换了一次计时会话 → **这是唯一允许表针归零的路径**。
     *
     * 不能只看 `displayedSeconds === 0` 来判断"换没换"：同一页面上从
     * 第 1 项换到第 2 项时，第 1 项可能已经走了一大截。用 id 比对才是准的。
     */
    if (checkinId !== activeCheckinIdRef.current) {
      activeCheckinIdRef.current = checkinId;
      anchorRef.current = initialTimerAnchor(monotonicNow());
      setElapsedSeconds(0);
    }

    if (!checkinId) return;
    // 服务端还没给秒数（刚开始、响应解不出来）→ 不锚定，让单调时钟自己走
    if (serverSeconds === null) return;
    reanchor(serverSeconds);
    // 只认"服务端给的秒数"与"哪一条打卡"，不依赖 startedAt（本地时间不可信）
  }, [checkinId, serverSeconds, reanchor]);

  // 每秒推进一次显示（只在 running 时）
  useEffect(() => {
    if (!isRunning || !checkinId) return;
    const tick = () => {
      applyReading();
    };
    tick();
    const timer = globalThis.setInterval(tick, 1000);
    return () => globalThis.clearInterval(timer);
  }, [isRunning, checkinId, applyReading]);

  // 心跳：每 30 秒一次（见方案 2.3 的自动暂停判定）
  useEffect(() => {
    if (!isRunning || !checkinId) return;

    let cancelled = false;
    const beat = async () => {
      const response = await heartbeatCheckin(checkinId);
      if (cancelled) return;
      if (response) {
        setHeartbeatFailed(false);
        /**
         * ⚠️ 这里**不再**写 `?? 0`：响应里没有秒数（`null`）时
         * `reanchor` 会跳过这一拍，锚点保持不动 —— 表针继续按单调时钟走。
         * 旧代码的 `?? 0` 正是"每 30 秒归零"的最后一步。
         */
        reanchor(response.checkin.actualSeconds);
        onSyncRef.current?.(response);
      } else {
        // 心跳失败不改本地计时：继续按单调时钟走，等下一次心跳把锚点拉回来
        setHeartbeatFailed(true);
      }
    };

    /**
     * ⚠️ **立刻同步一次，不要等 30 秒**（2026-10-29 用户报的 bug）。
     *
     * 用户原话："孩子端，作业，点开始，计时。这时，如果切换到其他页面再回到作业页面，
     * 计时又从 0 开始计时。"
     *
     * 根因：这一端重新挂载时，锚点从 0 起算，而它唯一的秒数来源是**页面数据**
     * （`actualSeconds`）。那份数据可能在 React Query 缓存里（`staleTime` 15 秒内不会重取），
     * 缓存里的值正是"刚开始时 ≈ 0" —— 于是表针从 0 重来，要等到下一次 30 秒心跳
     * 才会跳回真实值（用户看到的就是"从 0 重新计时"）。
     *
     * 心跳**就是**"服务端告诉我现在跑了多少秒"的官方接口（`recompute_checkin_seconds()`），
     * 所以挂载即打一次，表针立刻对齐；30 秒的周期只用于之后的对齐与自动暂停判定。
     * 多打一次心跳是幂等的（只写一条事件 + 更新 `last_heartbeat_at`）。
     */
    void beat();

    const timer = globalThis.setInterval(() => void beat(), HEARTBEAT_INTERVAL_SECONDS * 1000);
    return () => {
      cancelled = true;
      globalThis.clearInterval(timer);
    };
  }, [isRunning, checkinId, reanchor]);

  return { elapsedSeconds, isRunning, heartbeatFailed, lastSyncAt };
}
